import React, { useEffect, useRef, useState } from 'react';

import { useGetGroup } from '@vlabs/api/artemy/client';
import type {
  StreamAnalytics,
} from '@vlabs/api/artemy/model';
import {
  DeleteIcon, DetectionIcon, InfoCircleFilledIcon, PlusIcon,
} from '@vlabs/icons';
import { Control, useModal } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { useForm } from 'react-hook-form';

import {
  getAnalyticsTypeLabel,
  loadGroupOptions,
  nextAnalyticsLabel,
  parseAnalyticsLabel,
  STREAM_NAME_PATTERN,
  SynchronizeStatus,
} from '@vlabs/monokit/entities/sources';
import type { SourceGroupOption } from '@vlabs/monokit/entities/sources';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { selectMenuPortalStyles } from '@vlabs/monokit/shared/ui/select-portal';

import { useCreateStreamTranslation } from '../i18n';
import type { CreateStreamFormValues, CreateStreamProps } from '../types';
import st from './CreateStreamForm.module.sass';
import { NewAnalyticsModal } from './NewAnalyticsModal';
import type { NewAnalyticsValues } from './NewAnalyticsModal';

const displayName = 'CreateStreamForm';
const testid = buildTestid(displayName);

type CreateStreamFormFields = Pick<CreateStreamFormValues, 'name' | 'reference'>;

interface CreateStreamFormProps extends CreateStreamProps {
  defaultValues?: Partial<CreateStreamFormFields>;
  onCancel: VoidFunction;
  onSubmit: (data: CreateStreamFormValues) => Promise<void> | void;
  isPending: boolean;
}

interface AddedAnalyticsItem extends NewAnalyticsValues {
  id: number;
  label: string;
}

function CreateStreamForm({
  groupId,
  defaultValues = {},
  onCancel,
  onSubmit,
  isPending,
}: CreateStreamFormProps): React.JSX.Element {
  const { t } = useCreateStreamTranslation();
  const [streamType, setStreamType] = useState<'stream' | 'videofile'>('stream');
  const [groupOption, setGroupOption] = useState<SourceGroupOption | undefined>();
  const [addedAnalytics, setAddedAnalytics] = useState<AddedAnalyticsItem[]>([]);
  const analyticsNextIdRef = useRef(0);
  const newAnalyticsModal = useModal({ size: 'small' });

  const selectedGroupId = groupOption?.value ?? groupId;
  const { data: groupRecord } = useGetGroup(selectedGroupId);
  const inheritedAnalytics: StreamAnalytics[] = groupRecord?.analytics ?? [];
  // «Добавить» показывается только когда у группы нет своих ВА (макет 610)
  const canAddAnalytics = inheritedAnalytics.length === 0;

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<CreateStreamFormFields>({
    defaultValues: { name: '', reference: '', ...defaultValues },
  });

  useEffect(() => {
    loadGroupOptions().then((options) => {
      const preselected = options.find((option) => option.value === groupId);
      if (preselected) setGroupOption(preselected);
    });
  }, [groupId]);

  const handleAddAnalytics = (values: NewAnalyticsValues) => {
    analyticsNextIdRef.current += 1;
    setAddedAnalytics((prev) => [
      ...prev,
      {
        ...values,
        id: analyticsNextIdRef.current,
        label: nextAnalyticsLabel([...inheritedAnalytics, ...prev], values.type),
      },
    ]);
    newAnalyticsModal.close();
  };

  const removeAnalytics = (id: number) => {
    setAddedAnalytics((prev) => prev.filter((item) => item.id !== id));
  };

  const isSubmitDisabled = isPending
    || !groupOption
    // Бэк требует минимум одну ВА (валидация после мержа настроек группы) — 422 без них
    || (inheritedAnalytics.length === 0 && addedAnalytics.length === 0);

  const submit = handleSubmit(async (values: CreateStreamFormFields) => {
    if (!groupOption) return undefined;

    return onSubmit({
      name: values.name,
      reference: values.reference,
      type: streamType,
      group_id: groupOption.value,
      analytics: addedAnalytics.map((item) => ({
        type: item.type,
        label: item.label,
        handlerId: item.handlerId,
      })),
    });
  });

  // Суб-модалка рендерится порталом, но по React-дереву находится внутри формы —
  // её submit-событие всплывает сюда. Пропускаем чужие события.
  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    if (event.target !== event.currentTarget) return;
    submit(event).catch(() => undefined);
  };

  const analyticsItemTitle = (type: string, label: string | undefined): string => {
    const labelParts = parseAnalyticsLabel(label);

    return getAnalyticsTypeLabel(type)
      + (labelParts ? ` ${labelParts.number}` : '');
  };

  return (
    <form onSubmit={onFormSubmit}>
      <div className={st.Root}>
        <div className={st.Content}>
          <h5 className={st.Title}>
            {t('create_stream_form.title')}
          </h5>

          <div className={st.Field}>
            <Control.Input
              errorMessage={errors.name?.message}
              errors={errors}
              id="name"
              label={t('create_stream_form.name')}
              {...register('name', {
                pattern: {
                  message: t('create_stream_form.validation.name_pattern'),
                  value: STREAM_NAME_PATTERN,
                },
                required: t('create_stream_form.validation.required'),
              })}
              {...testid('name')}
            />
          </div>

          <div className={st.Field}>
            <Select
              classNamePrefix="CreateStreamType"
              label={t('create_stream_form.type')}
              onChange={(option) => setStreamType(option?.value ?? 'stream')}
              options={[
                { label: t('create_stream_form.stream_type_stream'), value: 'stream' },
                { label: t('create_stream_form.stream_type_videofile'), value: 'videofile' },
              ]}
              value={{ label: t(`create_stream_form.stream_type_${streamType}`), value: streamType }}
              {...testid('streamType')}
            />
          </div>

          <div className={st.Field}>
            <Control.Input
              errorMessage={errors.reference?.message}
              errors={errors}
              id="reference"
              label={t('create_stream_form.reference')}
              {...register('reference', { required: t('create_stream_form.validation.required') })}
              {...testid('reference')}
            />
          </div>

          <div className={st.Field}>
            <Select
              classNamePrefix="CreateStreamGroup"
              defaultOptions
              isAsync
              isClearable
              label={t('create_stream_form.group')}
              loadOptions={loadGroupOptions}
              menuPortalTarget={document.body}
              onChange={(option) => setGroupOption(option ?? undefined)}
              placeholder={t('create_stream_form.group')}
              styles={selectMenuPortalStyles}
              value={groupOption}
              {...testid('group')}
            />
          </div>

          <div className={st.AnalyticsSection}>
            <div className={st.AnalyticsHeader}>
              <span className={st.AnalyticsTitle}>
                {t('create_stream_form.analytics')}
              </span>
              {!!canAddAnalytics && (
                <Control.Button
                  disabled={isPending}
                  icon={<PlusIcon />}
                  kind="primary"
                  onClick={newAnalyticsModal.open}
                  size="small"
                  type="button"
                  variant="text"
                  {...testid('addAnalytics')}
                >
                  {t('create_stream_form.analytics_add')}
                </Control.Button>
              )}
            </div>

            {inheritedAnalytics.length > 0 && (
              <div className={st.Toast}>
                <InfoCircleFilledIcon className={st.ToastIcon} />
                <span className={st.ToastText}>
                  {t('create_stream_form.analytics_inherited_toast')}
                </span>
              </div>
            )}

            {inheritedAnalytics.length === 0 && addedAnalytics.length === 0 && (
              <div className={st.Toast}>
                <InfoCircleFilledIcon className={st.ToastIcon} />
                <span className={st.ToastText}>
                  {t('create_stream_form.analytics_empty_toast')}
                </span>
              </div>
            )}

            {inheritedAnalytics.map((item) => (
              <div
                className={st.AnalyticsItem}
                key={`${item.type}-${item.label ?? ''}`}
              >
                <span className={st.AnalyticsItemName}>
                  <DetectionIcon className={st.AnalyticsItemIcon} />
                  <span className={st.AnalyticsItemLabelInherited}>
                    {analyticsItemTitle(item.type, item.label)}
                  </span>
                </span>
                <SynchronizeStatus synchronized />
              </div>
            ))}

            {addedAnalytics.map((item) => {
              const key = String(item.id);

              return (
                <div
                  className={st.AnalyticsItem}
                  key={key}
                >
                  <span className={st.AnalyticsItemName}>
                    <DetectionIcon className={st.AnalyticsItemIcon} />
                    <span>{analyticsItemTitle(item.type, item.label)}</span>
                  </span>
                  <Control.IconButton
                    className={st.AnalyticsItemDelete}
                    disabled={isPending}
                    icon={<DeleteIcon />}
                    kind="negative"
                    onClick={() => removeAnalytics(item.id)}
                    shape="round"
                    size="small"
                    variant="flat"
                    {...testid(`removeAnalytics-${key}`)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className={st.Actions}>
          <Control.Button
            className={st.Action}
            disabled={isSubmitDisabled}
            isLoading={isPending}
            type="submit"
          >
            {t('create_stream_form.on_submit')}
          </Control.Button>

          <Control.Button
            className={st.Action}
            disabled={isPending}
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('create_stream_form.on_cancel')}
          </Control.Button>
        </div>
      </div>

      {newAnalyticsModal.wrap(
        <NewAnalyticsModal
          onCancel={newAnalyticsModal.close}
          onSubmit={handleAddAnalytics}
        />,
      )}
    </form>
  );
}

CreateStreamForm.displayName = displayName;

export { CreateStreamForm };
